import { useEffect, useState, useMemo } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Skeleton } from "../../components/ui/skeleton";
import EmptyHint from "../../components/dashboard/EmptyHint";
import { count, plural, joinParts, NO_VALUE } from "../../lib/vocabulary";
import { scopeSummary } from "../../lib/population";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Sparkles, TrendingUp, Receipt, CalendarOff, Activity, IndianRupee, User, Users2 } from "lucide-react";

const NAVY = "#0A1628";
const GOLD = "#C9A84C";
const PALETTE = ["#0A1628", "#C9A84C", "#3B82F6", "#10B981", "#F97316", "#EF4444", "#8B5CF6", "#06B6D4"];

/**
 * A slice smaller than this vanishes. RC-17 / F392: one dominant expense type
 * flattened every other category into a hairline, so a ₹40 000 Travel month
 * made a ₹1 200 Marketing spend literally invisible on the chart that exists to
 * show it. `minAngle` is recharts' scale guard — it borrows the difference from
 * the large slices, so the ordering and the tooltip stay exact while the small
 * categories stay findable. The legend below carries the real number for every
 * category, so nobody has to read an amount off an angle at all.
 */
const MIN_SLICE_ANGLE = 4;

const shortDate = (iso) => {
  if (!iso) return "";
  const [, m, d] = iso.split("-");
  return `${parseInt(d, 10)}/${parseInt(m, 10)}`;
};

const inrFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });

/** `₹1,200`, or the dash. Never `₹0` for a figure that did not arrive. */
function inr(value) {
  return value === null || value === undefined ? NO_VALUE : `₹${inrFmt.format(value)}`;
}

export default function AnalyticsPage() {
  const { user } = useAuth();
  const role = user?.role;
  const [scope, setScope] = useState("team");

  if (role === "sales_officer") return <MyAnalyticsPage />;

  const isCompany = role === "director" || role === "admin";

  // The scope toggle used to sit on its own row above the page, costing ~68px
  // of a 720px budget for one control. It now rides in the header row instead.
  const toolbar = (
    <div className="inline-flex rounded-lg border border-callisto bg-white p-0.5" data-testid="analytics-scope-toggle">
      <button
        type="button"
        onClick={() => setScope("team")}
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
          scope === "team" ? "bg-callisto-navy text-white" : "text-callisto-muted hover:text-callisto-navy"
        }`}
        data-testid="analytics-scope-team"
      >
        <Users2 size={13} />
        {isCompany ? "Company" : "Team"}
      </button>
      <button
        type="button"
        onClick={() => setScope("me")}
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
          scope === "me" ? "bg-callisto-navy text-white" : "text-callisto-muted hover:text-callisto-navy"
        }`}
        data-testid="analytics-scope-me"
      >
        <User size={13} />
        My performance
      </button>
    </div>
  );

  return scope === "me"
    ? <MyAnalyticsPage toolbar={toolbar} />
    : <TeamOrCompanyAnalyticsPage role={role} toolbar={toolbar} />;
}

function HeaderBadge({ icon: Icon, label, title, subtitle, right = null }) {
  return (
    // One row instead of three stacked blocks. The badge and title carry the
    // same information side by side and save ~50px, which is one chart row's
    // worth of the 720px budget.
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-callisto-gold/10 text-callisto-gold px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] shrink-0">
        <Icon size={11} />
        {label}
      </span>
      <h1 className="text-xl md:text-2xl font-bold text-callisto-navy tracking-tight">{title}</h1>
      {subtitle && <p className="text-xs text-callisto-muted w-full md:w-auto">{subtitle}</p>}
      {right && <div className="ml-auto shrink-0">{right}</div>}
    </div>
  );
}

/**
 * Chart height scales with the viewport rather than sitting at a fixed 240px.
 *
 * The target is a 1280x720 laptop, where the whole page must fit without
 * scrolling. A fixed height either overflows there or wastes space on a taller
 * screen; 18vh floors at 140px on short viewports and grows to 210px on tall
 * ones. ResponsiveContainer needs a sized parent, hence the wrapper.
 */
function ChartFrame({ children }) {
  return <div className="h-[clamp(140px,18vh,210px)]">{children}</div>;
}

function ChartCard({ title, subtitle, icon: Icon, children, testId }) {
  return (
    <div className="bg-white border border-callisto rounded-xl p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && (
          <div className="h-8 w-8 rounded-lg bg-callisto-navy/5 text-callisto-navy flex items-center justify-center">
            <Icon size={16} />
          </div>
        )}
        <div>
          <h3 className="text-sm font-bold text-callisto-navy">{title}</h3>
          {subtitle && <p className="text-[11px] text-callisto-muted">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

/**
 * The expense donut plus a value-bearing legend.
 *
 * Recharts' own `<Legend>` prints names only, which is why the sliver mattered:
 * a category with no readable slice had no readable number either. This renders
 * the amount and the share next to every name, so the chart shows the shape and
 * the list carries the facts.
 */
function ExpenseDonut({ data: rows }) {
  const total = rows.reduce((acc, r) => acc + (r.value || 0), 0);
  return (
    <div className="flex flex-col sm:flex-row items-center gap-3">
      <div className="w-full sm:w-1/2">
        <ChartFrame><ResponsiveContainer width="100%" height="100%">
          <PieChart>
            {/* Percentage radii, not px: the frame is 18vh, so a fixed
                88px radius overflows it on short viewports and the top
                of the ring clips against the card. Percentages scale
                with whatever height the frame actually gets. */}
            <Pie
              data={rows}
              dataKey="value"
              nameKey="name"
              innerRadius="52%"
              outerRadius="88%"
              paddingAngle={2}
              minAngle={MIN_SLICE_ANGLE}
            >
              {rows.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
            </Pie>
            <Tooltip formatter={(v) => [inr(v), "Amount"]} />
          </PieChart>
        </ResponsiveContainer></ChartFrame>
      </div>
      <ul className="w-full sm:w-1/2 space-y-1" data-testid="expense-legend">
        {rows.map((r, i) => (
          <li key={r.name} className="flex items-center gap-2 text-[11px]">
            <span
              className="h-2.5 w-2.5 rounded-sm shrink-0"
              style={{ backgroundColor: PALETTE[i % PALETTE.length] }}
            />
            <span className="text-callisto-navy truncate flex-1">{r.name}</span>
            <span className="font-semibold text-callisto-navy whitespace-nowrap">
              {inr(r.value)}
            </span>
            <span className="text-callisto-muted whitespace-nowrap w-9 text-right">
              {total ? `${Math.round((r.value / total) * 100)}%` : NO_VALUE}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MyAnalyticsPage({ toolbar = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/analytics/me")
      .then(({ data }) => !cancelled && setData(data))
      .catch((e) => !cancelled && setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const visitSeries = useMemo(
    () => (data?.visits_30d?.series || []).map((p) => ({ ...p, label: shortDate(p.date) })),
    [data]
  );
  const pieData = useMemo(
    () => (data?.expenses_month?.breakdown || []).map((b) => ({ name: b.type, value: b.amount })),
    [data]
  );
  // NULL, not four zeroes. `|| {Present: 0, Absent: 0, ...}` presented a failed
  // or absent fetch as a month in which this officer was recorded present on no
  // day at all — Pattern A, on the tile that sits closest to payroll.
  const att = data?.attendance_month ?? null;
  const leave = data?.leave_balance ?? null;

  return (
    <div className="space-y-4" data-testid="my-analytics-page">
      <HeaderBadge
        icon={Sparkles}
        label="My Analytics"
        title="Your performance snapshot"
        right={toolbar}
        subtitle={
          data
            ? joinParts([
                `${count(data.visits_30d?.total)} visits in last 30 days`,
                `${inr(data.expenses_month?.total)} expenses this month`,
              ])
            : "Loading…"
        }
      />

      {error && <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}

      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-64 rounded-xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <ChartCard
            title="My visits — last 30 days"
            subtitle={`${count(data?.visits_30d?.total)} total · pattern of doctor calls`}
            icon={TrendingUp}
            testId="my-analytics-visits-chart"
          >
            <ChartFrame><ResponsiveContainer width="100%" height="100%">
              <LineChart data={visitSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={4} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip formatter={(v) => [`${v} visits`, "Visits"]} />
                <Line type="monotone" dataKey="count" stroke={NAVY} strokeWidth={2.5} dot={{ r: 2 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer></ChartFrame>
          </ChartCard>

          <ChartCard
            title="My expenses — current month"
            subtitle={`${inr(data?.expenses_month?.total)} total · approved + submitted`}
            icon={Receipt}
            testId="my-analytics-expenses-chart"
          >
            {pieData.length === 0 ? (
              <EmptyHint
                Icon={Receipt}
                title="No expenses this month yet"
                hint="Approved and submitted claims for the current month appear here, split by type."
                cta={{ to: "/expenses/new", label: "Submit an expense" }}
                testId="my-analytics-expenses-empty"
              />
            ) : (
              <ExpenseDonut data={pieData} />
            )}
          </ChartCard>

          <ChartCard
            title="Leave balance"
            subtitle="Casual and Privilege — accrued · availed · balance"
            icon={CalendarOff}
            testId="my-analytics-leave-balance"
          >
            {/* P5/P8: exactly two types, PL computed. NEVER a fixed annual
                number — F391's "0 of 39 available days" was an entitlement
                total read off a document that never carried one, over a model
                in which no annual PL figure exists to read. */}
            {leave === null ? (
              <p className="text-sm text-callisto-muted text-center py-8" data-testid="leave-balance-unavailable">
                Leave balance did not load. This is not a balance of zero.
              </p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <BalancePill
                    title="Casual (CL)"
                    balance={leave.CL?.balance}
                    accrued={leave.CL?.accrued}
                    availed={leave.CL?.availed}
                    color="bg-sky-100 text-sky-700"
                    testId="leave-balance-cl"
                  />
                  <BalancePill
                    title="Privilege (PL)"
                    balance={leave.PL?.balance}
                    accrued={leave.PL?.accrued}
                    availed={leave.PL?.availed}
                    lapsed={leave.PL?.lapsed_ytd}
                    color="bg-emerald-100 text-emerald-700"
                    testId="leave-balance-pl"
                  />
                </div>
                {leave.PL?.lapsed_ytd > 0 && (
                  <p className="mt-2 text-[11px] text-orange-700">
                    {plural(leave.PL.lapsed_ytd, "PL day")} lapsed this year at the{" "}
                    {count(leave.PL.cap)}-day ceiling.
                  </p>
                )}
              </>
            )}
          </ChartCard>

          <ChartCard
            title="Attendance — current month"
            subtitle="Days reported, by status"
            icon={Activity}
            testId="my-analytics-attendance"
          >
            {att === null ? (
              <p className="text-sm text-callisto-muted text-center py-8" data-testid="attendance-unavailable">
                Attendance did not load. These are not zero days.
              </p>
            ) : (
              <div className="grid grid-cols-4 gap-3">
                {/* "Not checked in", never "Absent": nobody wrote a record, and
                    the app does not know why (RC-11). The key stays `Absent`
                    because that is what the server reports. */}
                <AttendanceTile label="Present" value={att.Present} color="bg-emerald-50 text-emerald-700" />
                <AttendanceTile label="Not checked in" value={att.Absent} color="bg-amber-50 text-amber-700" />
                <AttendanceTile label="On Leave" value={att["On Leave"]} color="bg-sky-50 text-sky-700" />
                <AttendanceTile label="Holiday" value={att.Holiday} color="bg-slate-50 text-slate-700" />
              </div>
            )}
          </ChartCard>
        </div>
      )}
    </div>
  );
}

/**
 * accrued · availed · balance (· lapsed) — the four numbers P5/P8 requires.
 * `count()` throughout, so a key the response did not carry reads as the dash
 * and a real zero reads as 0.
 */
function BalancePill({ title, balance, accrued, availed, lapsed, color, testId }) {
  return (
    <div className={`rounded-lg px-3 py-3 text-center ${color}`} data-testid={testId}>
      <p className="text-2xl font-bold">{count(balance)}</p>
      <p className="text-[11px] uppercase font-semibold tracking-wider mt-0.5">
        {title} · balance
      </p>
      <p className="text-[10px] mt-1 opacity-80">
        {joinParts([
          `accrued ${count(accrued)}`,
          `availed ${count(availed)}`,
          lapsed === undefined || lapsed === null ? null : `lapsed ${count(lapsed)}`,
        ])}
      </p>
    </div>
  );
}

function AttendanceTile({ label, value, color }) {
  return (
    <div className={`rounded-lg px-3 py-3 text-center ${color}`}>
      <p className="text-2xl font-bold">{count(value)}</p>
      <p className="text-[11px] uppercase font-semibold tracking-wider mt-0.5">{label}</p>
    </div>
  );
}

function TeamOrCompanyAnalyticsPage({ role, toolbar = null }) {
  const isCompany = role === "director" || role === "admin";
  const scopeLabel = isCompany ? "Company analytics" : "Team analytics";
  const [trend, setTrend] = useState(null);
  const [expenses, setExpenses] = useState(null);
  const [leaves, setLeaves] = useState(null);
  const [dcr, setDcr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get("/analytics/visits-trend?days=30"),
      api.get("/analytics/expenses-by-type"),
      api.get("/analytics/leave-usage"),
      api.get("/analytics/dcr-rate?days=30"),
    ])
      .then(([t, e, l, d]) => {
        if (cancelled) return;
        setTrend(t.data);
        setExpenses(e.data);
        setLeaves(l.data);
        setDcr(d.data);
      })
      .catch((e) => !cancelled && setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const trendSeries = useMemo(
    () => (trend?.series || []).map((p) => ({ ...p, label: shortDate(p.date) })),
    [trend]
  );
  // Non-working days are DROPPED from the line, not plotted as 0%. A Sunday on
  // which nobody was expected to file is not a day on which nobody filed, and
  // drawing it as a floor-scraping zero is the chart half of the denominator
  // defect the average below fixes.
  const dcrSeries = useMemo(
    () => (dcr?.series || [])
      .filter((p) => p.working_day !== false)
      .map((p) => ({ ...p, label: shortDate(p.date) })),
    [dcr]
  );
  const pieData = useMemo(
    () => (expenses?.breakdown || []).map((b) => ({ name: b.type, value: b.amount })),
    [expenses]
  );

  // RC-9. `total_sos` counts FIELD_ROLES company-wide — Sales Officers AND
  // Sales Managers — or, for a manager, his reports plus himself. Calling
  // either of those "sales officers" is the mislabel this rule exists for, and
  // the response carries a total without a role breakdown, so the only honest
  // words are the ones that describe the set rather than name a role.
  const scopeNoun = isCompany
    ? "field staff in scope (Officers + Sales Mgrs)"
    : `${dcr?.total_sos === 1 ? "person" : "people"} in scope (your reports + you)`;

  // The leaderboard is capped at 10 by the backend, so "top 10" is a promise
  // the data keeps only when there ARE ten. Three bars under a "top 10"
  // heading reads as seven people with no leave rather than a company of three.
  const leaveRows = leaves?.series?.length ?? 0;
  const leaveSubtitle =
    leaveRows >= 10
      ? "Approved leave days · top 10"
      : `Approved leave days · ${plural(leaveRows, "person", "people")} with leave this year`;

  return (
    <div className="space-y-4" data-testid="team-analytics-page">
      <HeaderBadge
        icon={Sparkles}
        label={scopeLabel}
        title={isCompany ? "Company-wide performance" : "Your team's performance"}
        right={toolbar}
        subtitle={
          dcr
            ? joinParts([
                scopeSummary(dcr.total_sos, scopeNoun),
                `${count(dcr.avg_rate_pct)}% avg DCR rate`,
                `${count(trend?.total)} visits in 30d`,
              ])
            : "Loading…"
        }
      />

      {error && <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}

      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-64 rounded-xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <ChartCard
            title="Visits per day — last 30 days"
            subtitle={`${count(trend?.total)} total visits`}
            icon={TrendingUp}
            testId="analytics-visits-trend-chart"
          >
            <ChartFrame><ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={4} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip formatter={(v) => [`${v} visits`, "Visits"]} />
                <Line type="monotone" dataKey="count" stroke={NAVY} strokeWidth={2.5} dot={{ r: 2 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer></ChartFrame>
          </ChartCard>

          <ChartCard
            title="Expenses by type — current month"
            subtitle={`${inr(expenses?.total)} approved + submitted`}
            icon={IndianRupee}
            testId="analytics-expenses-pie"
          >
            {pieData.length === 0 ? (
              <EmptyHint
                Icon={IndianRupee}
                title="No expenses this month yet"
                hint="Approved and submitted claims for the current month appear here, split by type."
                cta={{ to: "/expenses", label: "Open expenses" }}
                testId="analytics-expenses-empty"
              />
            ) : (
              <ExpenseDonut data={pieData} />
            )}
          </ChartCard>

          <ChartCard
            title="Leave usage — this year"
            subtitle={leaveSubtitle}
            icon={CalendarOff}
            testId="analytics-leave-usage-chart"
          >
            {/* RC-15: the leaderboard had no empty state — a bare sentence with
                no icon, no explanation and nowhere to go. Same shape as every
                other empty collection in the app now. */}
            {!leaveRows ? (
              <EmptyHint
                Icon={CalendarOff}
                title="No approved leave yet this year"
                hint="Once a leave is approved it is counted here, per person, with any loss-of-pay days shown separately."
                cta={{ to: "/leaves", label: "Open leaves" }}
                testId="analytics-leave-usage-empty"
              />
            ) : (
              <ChartFrame><ResponsiveContainer width="100%" height="100%">
                <BarChart data={leaves.series} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={110} />
                  <Tooltip formatter={(v, n) => [`${v} days`, n === "lop_days" ? "LOP" : "Total"]} />
                  {/* `minPointSize` is the bar-chart twin of the donut's
                      `minAngle`: a one-day bar beside a thirty-day bar rounded
                      to nothing and read as no leave at all. */}
                  <Bar dataKey="days" fill={GOLD} radius={[0, 6, 6, 0]} minPointSize={2} />
                  <Bar dataKey="lop_days" fill="#EF4444" radius={[0, 6, 6, 0]} minPointSize={2} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </BarChart>
              </ResponsiveContainer></ChartFrame>
            )}
          </ChartCard>

          <ChartCard
            title="DCR submission rate — last 30 days"
            // NAME THE DENOMINATOR. The rate is the share of people in scope
            // who logged at least one visit, and it is now averaged over
            // WORKING days — Sundays and the holiday master excluded — which is
            // the denominator team attendance has always used. Averaged over
            // calendar days it read roughly 13% low, and the two percentages
            // sat on one screen inviting a comparison neither supported.
            subtitle={joinParts([
              `Avg ${count(dcr?.avg_rate_pct)}%`,
              dcr?.working_days === undefined || dcr?.working_days === null
                ? "over the days returned — working-day denominator not reported by this backend"
                : `over ${plural(dcr.working_days, "working day")} (Sundays and holidays excluded)`,
              "share of people in scope logging ≥1 visit",
            ])}
            icon={Activity}
            testId="analytics-dcr-rate-chart"
          >
            <ChartFrame><ResponsiveContainer width="100%" height="100%">
              <LineChart data={dcrSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={4} />
                <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} unit="%" />
                <Tooltip formatter={(v) => [`${v}%`, "DCR rate"]} />
                <Line type="monotone" dataKey="rate_pct" stroke={GOLD} strokeWidth={2.5} dot={{ r: 2 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer></ChartFrame>
          </ChartCard>
        </div>
      )}
    </div>
  );
}
